// ============================================================================
// WORK ORDER SERVICE - Post-Quote Execution Bridge (Workstream 3)
// ----------------------------------------------------------------------------
// Turns a won quote into an executable work order. Uses the dependency graph
// (sequence + dependsOn) generated during scope analysis to propose phased
// execution batches, trade assignments, and material schedules.
//
// Triggered automatically when a project is marked WON. The AI proposes the
// initial work order; the founder reviews and adjusts before mobilisation.
// ============================================================================

import { DataService } from './data-service';

// ── Trade detection from scope item content ──────────────────────────────────
const TRADE_PATTERNS = [
    { trade: 'waterproofing', pattern: /membrane|waterproof|seal|tanking|wet.?area|moisture.?barrier/i },
    { trade: 'concrete',      pattern: /concrete|spall|render|epoxy|grout|crack.?repair|patch/i },
    { trade: 'roofing',       pattern: /roof|gutter|flash|sark|downpipe|colorbond|ridge.?cap/i },
    { trade: 'painting',      pattern: /paint|coat|primer|stain|dulux|sealer/i },
    { trade: 'scaffolding',   pattern: /scaffold|swing.?stage|boom|hoist|elevated.?work/i },
    { trade: 'demolition',    pattern: /demo|strip|remove|break.?out|pull|clear/i },
    { trade: 'tiling',        pattern: /tile|grout|adhesive|substrate/i },
    { trade: 'plumbing',      pattern: /plumb|pipe|drain|tap|valve/i },
    { trade: 'electrical',    pattern: /electr|wire|switch|power|circuit|cable|light/i },
    { trade: 'carpentry',     pattern: /timber|wood|frame|door|window|joiner/i },
    { trade: 'structural',    pattern: /structur|beam|column|slab|foundation|rebar|reinforc/i },
    { trade: 'general',       pattern: /prelim|site.?estab|clean|mobilis/i },
];

function detectTrade(title, description) {
    const text = `${title || ''} ${description || ''}`.toLowerCase();
    for (const { trade, pattern } of TRADE_PATTERNS) {
        if (pattern.test(text)) return trade;
    }
    return 'general';
}

// ── Material extraction from cost breakdowns ─────────────────────────────────
function extractMaterials(scopeItem) {
    const materials = [];
    const breakdown = scopeItem.costBreakdown;
    if (!breakdown?.materials) return materials;

    for (const mat of breakdown.materials) {
        if (mat.description && mat.total > 0) {
            materials.push({
                materialName: mat.description,
                quantity: mat.qty || 0,
                unit: mat.unit || 'item',
                unitCost: mat.unitRate || 0,
                totalCost: mat.total,
                // Default lead times by material type
                leadTimeDays: estimateLeadTime(mat.description),
            });
        }
    }
    return materials;
}

function estimateLeadTime(materialName) {
    const name = (materialName || '').toLowerCase();
    if (name.match(/membrane|waterproof|sika|mapei/)) return 5;
    if (name.match(/scaffold|formwork|steel/)) return 7;
    if (name.match(/timber|wood|frame/)) return 3;
    if (name.match(/paint|coat|primer|stain/)) return 2;
    if (name.match(/tile|grout|adhesive/)) return 5;
    if (name.match(/custom|fabricat|speciali/)) return 14;
    return 3; // default
}

// ============================================================================
// PHASE BUILDER - Topological grouping from dependency graph
// ============================================================================

/**
 * Groups scope items into execution phases using their `sequence` and `dependsOn`.
 * Items with the same sequence number are grouped together (parallel execution).
 * Dependencies are validated: an item's phase is at least 1 + max(dependency phases).
 *
 * @param {Array} scopeItems - Normalized scope items with `id`, `sequence`, `dependsOn`
 * @returns {Array} phases - [{ phaseNumber, name, scopeItemIds, trades, estimatedDays }]
 */
export function buildPhases(scopeItems) {
    if (!scopeItems || scopeItems.length === 0) return [];

    const idToItem = {};
    scopeItems.forEach(item => { idToItem[item.id] = item; });

    // Build adjacency: for each item, which phase must it land in?
    // Phase = max(sequence, 1 + max(phases of all dependencies))
    const idToPhase = {};

    function resolvePhase(itemId, visited = new Set()) {
        if (idToPhase[itemId] !== undefined) return idToPhase[itemId];
        if (visited.has(itemId)) return 1; // cycle guard
        visited.add(itemId);

        const item = idToItem[itemId];
        if (!item) return 1;

        const deps = (item.dependsOn || []).filter(depId => idToItem[depId]);
        let minPhase = item.sequence || 1;

        for (const depId of deps) {
            const depPhase = resolvePhase(depId, visited);
            minPhase = Math.max(minPhase, depPhase + 1);
        }

        idToPhase[itemId] = minPhase;
        return minPhase;
    }

    // Resolve all phases
    scopeItems.forEach(item => resolvePhase(item.id));

    // Group items by phase
    const phaseGroups = {};
    scopeItems.forEach(item => {
        const phase = idToPhase[item.id] || 1;
        if (!phaseGroups[phase]) phaseGroups[phase] = [];
        phaseGroups[phase].push(item);
    });

    // Build phase objects
    const phaseNumbers = Object.keys(phaseGroups).map(Number).sort((a, b) => a - b);
    return phaseNumbers.map((num, idx) => {
        const items = phaseGroups[num];
        const trades = [...new Set(items.map(item => detectTrade(item.title, item.description)))];
        const totalAmount = items.reduce((sum, item) => sum + (parseFloat(String(item.amount || 0).replace(/,/g, '')) || 0), 0);

        return {
            phaseNumber: idx + 1,
            sequenceGroup: num,
            name: generatePhaseName(items, trades, idx),
            scopeItemIds: items.map(item => item.id),
            trades,
            itemCount: items.length,
            totalAmount: Math.round(totalAmount * 100) / 100,
            estimatedDays: estimatePhaseDuration(items, trades),
        };
    });
}

function generatePhaseName(items, trades, idx) {
    if (idx === 0 && trades.includes('general')) return 'Site Setup & Mobilisation';
    if (trades.length === 1) {
        const TRADE_NAMES = {
            waterproofing: 'Waterproofing Works', concrete: 'Concrete Repairs',
            roofing: 'Roofing Works', painting: 'Painting & Coatings',
            scaffolding: 'Scaffolding & Access', demolition: 'Demolition & Strip-out',
            tiling: 'Tiling Works', plumbing: 'Plumbing Works',
            electrical: 'Electrical Works', carpentry: 'Carpentry & Joinery',
            structural: 'Structural Works', general: 'General Works',
        };
        return TRADE_NAMES[trades[0]] || `Phase ${idx + 1}`;
    }
    if (items.length === 1) return items[0].title || `Phase ${idx + 1}`;
    return `Phase ${idx + 1} - ${trades.slice(0, 3).join(', ')}`;
}

function estimatePhaseDuration(items, trades) {
    // Rough heuristic: base 1 day per item, min 1, max 15
    const base = Math.max(1, Math.ceil(items.length * 1.5));
    // Scaffolding adds setup time
    if (trades.includes('scaffolding')) return Math.min(15, base + 2);
    // Waterproofing needs cure time
    if (trades.includes('waterproofing')) return Math.min(15, base + 3);
    return Math.min(15, base);
}

// ============================================================================
// WORK ORDER CREATION
// ============================================================================

/**
 * Create a draft work order from a won project's scope items.
 * Deterministic (no AI call) - uses the dependency graph from scope analysis.
 *
 * @param {Object} project - The won project object
 * @returns {Promise<string>} Work order ID
 */
export async function createWorkOrderFromProject(project) {
    if (!project?.id || !project.scope) {
        throw new Error('Project must have id and scope');
    }

    const scopeItems = project.scope || [];
    const phases = buildPhases(scopeItems);
    const totalValue = scopeItems.reduce((s, i) => s + (parseFloat(String(i.amount || 0).replace(/,/g, '')) || 0), 0);

    const workOrder = {
        projectId: project.id,
        status: 'draft',
        projectTitle: project.formData?.address || project.title || 'Untitled Project',
        totalValue: Math.round(totalValue * 100) / 100,
        phases,
        scopeItemCount: scopeItems.length,
        // Dependency summary for quick display
        hasDependencies: scopeItems.some(i => i.dependsOn?.length > 0),
        phaseCount: phases.length,
        estimatedTotalDays: phases.reduce((s, p) => s + p.estimatedDays, 0),
    };

    const woId = await DataService.workOrders.save(workOrder);

    // Create trade assignments from phase data
    for (const phase of phases) {
        for (const itemId of phase.scopeItemIds) {
            const item = scopeItems.find(i => i.id === itemId);
            if (!item) continue;
            const trade = detectTrade(item.title, item.description);
            await DataService.tradeAssignments.save({
                workOrderId: woId,
                scopeItemId: itemId,
                tradeType: trade,
                contractorId: null, // founder assigns later
                status: 'unassigned',
                phaseNumber: phase.phaseNumber,
                itemTitle: item.title,
            });
        }
    }

    // Create material schedules from cost breakdowns
    for (const item of scopeItems) {
        const materials = extractMaterials(item);
        for (const mat of materials) {
            await DataService.materialSchedules.save({
                workOrderId: woId,
                scopeItemId: item.id,
                materialName: mat.materialName,
                quantity: mat.quantity,
                unit: mat.unit,
                unitCost: mat.unitCost,
                totalCost: mat.totalCost,
                leadTimeDays: mat.leadTimeDays,
                status: 'pending', // pending → ordered → delivered
                orderDate: null,
                deliveryDate: null,
            });
        }
    }

    console.log(`[WorkOrder] Created draft WO ${woId}: ${phases.length} phases, ${scopeItems.length} items, $${totalValue.toLocaleString()}`);
    return woId;
}

// ============================================================================
// EXPORTS
// ============================================================================

export const WorkOrderService = {
    buildPhases,
    createWorkOrderFromProject,
    detectTrade,
    extractMaterials,
};
